import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { extractionApi, type ExtractionJob } from '@/api/extraction'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ModelBadge } from '@/components/ui/model-badge'
import { formatDateTime } from '@/lib/utils'
import { Square } from 'lucide-react'

interface Props {
  projectId: string
  onViewResults?: (jobId: string) => void
}

function statusVariant(status: string): string {
  if (status === 'completed') return 'text-green-600 bg-green-50'
  if (status === 'failed') return 'text-red-600 bg-red-50'
  if (status === 'running') return 'text-blue-600 bg-blue-50'
  if (status === 'queued') return 'text-gray-600 bg-gray-50'
  if (status === 'stopped') return 'text-yellow-600 bg-yellow-50'
  return 'text-gray-600 bg-gray-50'
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '--'
  const start = new Date(startedAt).getTime()
  const end = completedAt ? new Date(completedAt).getTime() : Date.now()
  const sec = Math.round((end - start) / 1000)
  if (sec < 60) return `${sec}s`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`
}

export default function ExtractionRuns({ projectId, onViewResults }: Props) {
  const queryClient = useQueryClient()

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['extraction-jobs', projectId],
    queryFn: () => extractionApi.listJobs(projectId),
    refetchInterval: 5000,
  })

  const stopJobMutation = useMutation({
    mutationFn: (jobId: string) => extractionApi.stopJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['extraction-jobs', projectId] })
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
      queryClient.invalidateQueries({ queryKey: ['results', projectId] })
    },
  })

  // Sort descending by created_at (newest first)
  const sortedJobs = [...jobs].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading runs...</div>
  }

  if (sortedJobs.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg">
        <p className="text-muted-foreground">No extraction runs yet.</p>
        <p className="text-sm text-muted-foreground mt-1">
          Process documents from the Documents tab to create a run.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Extraction Runs</h2>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Run #</th>
                <th className="text-left p-3 font-medium">Date</th>
                <th className="text-left p-3 font-medium">Model</th>
                <th className="text-left p-3 font-medium">Mode</th>
                <th className="text-center p-3 font-medium">Documents</th>
                <th className="text-center p-3 font-medium">Failed</th>
                <th className="text-left p-3 font-medium">Duration</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-right p-3 font-medium">Credits</th>
                <th className="text-left p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((job, idx) => {
                const runNumber = sortedJobs.length - idx
                const isActive = job.status === 'running' || job.status === 'queued'
                return (
                  <tr key={job.id} className="border-t hover:bg-muted/20">
                    <td className="p-3 font-medium">#{runNumber}</td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      {formatDateTime(job.created_at)}
                    </td>
                    <td className="p-3">
                      {job.selected_model ? (
                        <ModelBadge modelName={job.selected_model} />
                      ) : (
                        <span className="text-muted-foreground">--</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="text-xs">
                        {job.extraction_mode === 'batch' ? 'Batch' : 'Per-field'}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      {job.processed_documents}/{job.total_documents}
                    </td>
                    <td className="p-3 text-center">
                      {job.failed_documents > 0 ? (
                        <span className="text-red-600 font-medium">{job.failed_documents}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      {formatDuration(job.started_at, job.completed_at)}
                    </td>
                    <td className="p-3">
                      <Badge
                        variant="outline"
                        className={`text-xs ${statusVariant(job.status)}`}
                      >
                        {job.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right text-muted-foreground">
                      {job.credits_consumed != null
                        ? `$${Number(job.credits_consumed).toFixed(2)}`
                        : job.credits_estimated != null
                          ? `~$${Number(job.credits_estimated).toFixed(2)}`
                          : '--'}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {onViewResults && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onViewResults(job.id)}
                          >
                            View Results
                          </Button>
                        )}
                        {isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              if (confirm('Stop this extraction job? Documents already processed will keep their results.')) {
                                stopJobMutation.mutate(job.id)
                              }
                            }}
                            disabled={stopJobMutation.isPending}
                          >
                            <Square className="h-3.5 w-3.5 mr-1" />
                            Stop
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
